/**
 * Projects API endpoints
 */
import { apiClient } from '../client'

export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled'
export type Priority = 'low' | 'medium' | 'high' | 'urgent'

export interface Project {
  id: number
  name: string
  description: string | null
  status: ProjectStatus
  priority: Priority
  start_date: string | null
  end_date: string | null
  project_manager_id: number | null
  budget: string | null
  actual_cost: string | null
  progress: number
  organization_id: number | null
  created_at: string
  updated_at: string
  metadata?: Record<string, any>
}

export interface ProjectCreate {
  name: string
  description?: string
  status?: ProjectStatus
  priority?: Priority
  start_date?: string
  end_date?: string
  project_manager_id?: number
  budget?: string
  organization_id?: number
  team_ids?: number[]
  metadata?: Record<string, any>
}

export interface ProjectUpdate {
  name?: string
  description?: string
  status?: ProjectStatus
  priority?: Priority
  start_date?: string
  end_date?: string
  project_manager_id?: number
  budget?: string
  actual_cost?: string
  progress?: number
  team_ids?: number[]
  metadata?: Record<string, any>
}

export interface ProjectStats {
  project_id: number
  total_tasks: number
  tasks_by_status: Record<string, number>
  progress: number
  budget: string | null
  actual_cost: string | null
}

export const projectsApi = {
  /**
   * List projects
   */
  async list(params?: {
    project_manager_id?: number
    status?: string
    skip?: number
    limit?: number
    include_deleted?: boolean
  }): Promise<Project[]> {
    const response = await apiClient.get<Project[]>('/api/v1/projects', { params })
    return response.data
  },

  /**
   * Get project by ID
   */
  async get(id: number): Promise<Project> {
    const response = await apiClient.get<Project>(`/api/v1/projects/${id}`)
    return response.data
  },

  /**
   * Create project
   */
  async create(data: ProjectCreate): Promise<Project> {
    const response = await apiClient.post<Project>('/api/v1/projects', data)
    return response.data
  },

  /**
   * Update project
   */
  async update(id: number, data: ProjectUpdate): Promise<Project> {
    const response = await apiClient.put<Project>(`/api/v1/projects/${id}`, data)
    return response.data
  },

  /**
   * Delete project
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/projects/${id}`)
  },

  /**
   * Get project statistics
   */
  async getStats(id: number): Promise<ProjectStats> {
    const response = await apiClient.get<ProjectStats>(`/api/v1/projects/${id}/stats`)
    return response.data
  },

  /**
   * Restore a soft-deleted project (superuser only)
   */
  async restore(id: number): Promise<Project> {
    const response = await apiClient.post<Project>(`/api/v1/projects/${id}/restore`)
    return response.data
  },
}

